import { useState } from 'react';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, api, type PeriodDetail } from '../lib/api';
import { ErrorNote, SectionHeading, Spinner } from '../components/ui';
import { Related } from '../components/Related';
import { DeleteRecord } from '../components/DeleteRecord';
import { Confirm } from '../components/Confirm';
import { useIsPlatformAdmin } from '../lib/useSession';

function message(error: unknown, fallback: string): string {
  return error instanceof ApiError ? error.message : fallback;
}

/**
 * One period, and the classes whose register it is a column of.
 *
 * The Periods table reported "used by 3 classes" and gave you no way to find
 * out which three. This is that number opened up, and the only place a period
 * can be put onto a class without going to the class first.
 */
export function ManagePeriodDetail() {
  const { periodId } = useParams({ from: '/app/manage/periods/$periodId' });
  const queryClient = useQueryClient();

  const detail = useQuery({
    queryKey: ['period', periodId],
    queryFn: () => api.admin.period(periodId),
  });
  const classes = useQuery({ queryKey: ['classes'], queryFn: api.classes.list });

  /*
   * A period is a register column, so attaching or detaching one changes the
   * shape of a marking screen. ['periods'] carries the count this page opens
   * up, ['class'] is the class detail page's own list, and ['grid'] is the
   * register itself, matched by prefix across every class and date cached.
   */
  const refresh = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['period', periodId] }),
      queryClient.invalidateQueries({ queryKey: ['periods'] }),
      queryClient.invalidateQueries({ queryKey: ['classes'] }),
      queryClient.invalidateQueries({ queryKey: ['class'] }),
      queryClient.invalidateQueries({ queryKey: ['grid'] }),
    ]);
  };

  const [query, setQuery] = useState('');

  /*
   * One request per class, in sequence rather than at once, so a failure stops
   * instead of firing the rest. onSettled, not onSuccess: a partial failure
   * still has to show which ones did take.
   */
  const attach = useMutation({
    mutationFn: async (classIds: string[]) => {
      for (const classId of classIds) await api.admin.attachPeriod(classId, periodId);
    },
    onSettled: refresh,
    onSuccess: () => setQuery(''),
  });

  const detach = useMutation({
    mutationFn: (classPeriodId: string) => api.admin.detachPeriod(classPeriodId),
    onSuccess: refresh,
  });

  if (detail.isLoading) return <Spinner />;
  if (detail.isError || !detail.data) {
    return (
      <div>
        <Link to="/manage/classes" className="eyebrow">
          Back to classes
        </Link>
        <div className="mt-4">
          <ErrorNote message={message(detail.error, 'Could not load this period.')} />
        </div>
      </div>
    );
  }

  const period = detail.data;
  const attachedIds = new Set(period.classes.map((row) => row.classId));

  /*
   * Retired classes are not offered: a class nobody will mark again is not
   * somewhere to add a column. Ones already attached stay in the list above.
   */
  const candidates = (classes.data?.classes ?? [])
    .filter((klass) => klass.isActive && !attachedIds.has(klass.id))
    .map((klass) => ({ id: klass.id, label: klass.name }));

  return (
    <div className="grid gap-8">
      <div>
        <Link to="/manage/classes" className="eyebrow hover:text-ink">
          Back to classes
        </Link>
        <div className="mt-2">
          <PeriodHeader period={period} onDone={refresh} />
        </div>
      </div>

      <Related
        title="Marked on"
        description="Every class whose register carries this as a column. Detaching it here drops the column from future registers and leaves every mark already taken against it exactly where it is."
        count={`${period.classes.length} ${period.classes.length === 1 ? 'class' : 'classes'}`}
        error={detach.error ? message(detach.error, 'Could not detach that class.') : null}
        label="Add to a class"
        options={candidates}
        query={query}
        onQuery={setQuery}
        empty="Already on every active class."
        searchPlaceholder="class name"
        disabled={attach.isPending}
        verb="Add"
        applying={attach.isPending}
        note={
          attach.error ? (
            <div className="mt-3">
              <ErrorNote
                message={message(
                  attach.error,
                  'Could not add it to every class. The list above shows which ones took.',
                )}
              />
            </div>
          ) : null
        }
        onApply={(ids, done) => attach.mutate(ids, { onSuccess: done })}
      >
        <ul className="mb-4">
          {period.classes.map((row) => (
            <li
              key={row.id}
              className="flex flex-wrap items-center gap-3 border-b border-rule py-2 text-sm last:border-0"
            >
              <Link
                to="/manage/classes/$classId"
                params={{ classId: row.classId }}
                className={
                  row.isActive ? 'hover:underline' : 'text-ink-faint line-through hover:text-ink'
                }
              >
                {row.name}
              </Link>
              {row.isActive ? null : <span className="eyebrow">retired</span>}
              <span className="ml-auto font-mono text-xs text-ink-soft tabular">
                column {row.sortOrder + 1}
              </span>
              <Confirm
                trigger="Detach"
                title={`Take ${period.name} off ${row.name}?`}
                description="The column leaves that class's future registers. Registers already marked keep it."
                confirmLabel="Detach"
                pendingLabel="Detaching…"
                disabled={detach.isPending}
                onConfirm={() => detach.mutateAsync(row.id)}
                errorFallback="Could not detach that class."
              />
            </li>
          ))}
          {period.classes.length === 0 ? (
            <li className="py-1.5 text-sm text-ink-soft">
              Not on any class yet, so nothing is marked against it.
            </li>
          ) : null}
        </ul>
      </Related>
    </div>
  );
}

/**
 * The period's name, and whether it is still offered.
 *
 * The name is the heading of a register column, so renaming rewrites how every
 * date already marked against it reads. It is still worth having: the
 * alternative to fixing a typo was retiring the period and creating a new one,
 * which splits the history in two.
 */
function PeriodHeader({ period, onDone }: { period: PeriodDetail; onDone: () => void }) {
  const [name, setName] = useState(period.name);
  const navigate = useNavigate();
  const canRestore = useIsPlatformAdmin();

  const save = useMutation({
    mutationFn: (body: Record<string, unknown>) => api.admin.updatePeriod(period.id, body),
    onSuccess: onDone,
  });

  /*
   * Separate from `save` because it ends differently. A deleted period is no
   * longer visible to the administrator who deleted it, so staying on its page
   * would refetch into a 404. Restoring stays put, because the record is there
   * to read again.
   */
  const remove = useMutation({
    mutationFn: (isDeleted: boolean) => api.admin.updatePeriod(period.id, { isDeleted }),
    onSuccess: async (_result, isDeleted) => {
      await onDone();
      if (isDeleted) await navigate({ to: '/manage/classes' });
    },
  });

  const unchanged = name.trim() === period.name || name.trim() === '';

  return (
    <div className="grid gap-4">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="eyebrow mb-1.5">Period</p>
          <h1 className="text-[28px] leading-none font-semibold tracking-[-0.02em]">
            {period.name}
          </h1>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          {/* A deleted record reads "deleted", not "retired deleted": the second
              stage is the one it is in, and DeleteRecord says so below. */}
          {period.isActive || period.deletedAt ? null : <span className="eyebrow">retired</span>}
          {period.deletedAt ? null : period.isActive ? (
            <Confirm
              trigger="Retire"
              title={`Retire ${period.name}?`}
              description="It leaves every future register in this organization. Registers already marked keep it."
              confirmLabel="Retire"
              pendingLabel="Retiring…"
              disabled={save.isPending}
              onConfirm={() => save.mutateAsync({ isActive: false })}
              errorFallback="Could not retire this period."
            />
          ) : (
            <button
              type="button"
              className="btn"
              disabled={save.isPending}
              title="Bring this period back onto new registers"
              onClick={() => save.mutate({ isActive: true })}
            >
              Restore
            </button>
          )}
          {/* Deleting is a second step past retiring, so it is offered only
              once the first has been taken. */}
          {period.isActive ? null : (
            <DeleteRecord
              noun="period"
              markCount={period.markCount}
              deleted={Boolean(period.deletedAt)}
              canRestore={canRestore}
              onDelete={() => remove.mutateAsync(true)}
              onRestore={() => remove.mutate(false)}
              pending={remove.isPending}
              error={remove.error ? message(remove.error, 'Could not change that.') : null}
            />
          )}
        </div>
      </div>

      <section>
        <SectionHeading
          title="Name"
          description="This is the column heading on every register already marked against it, so renaming changes how those dates read."
        />
        <form
          className="flex flex-wrap items-end gap-3"
          onSubmit={(e) => {
            e.preventDefault();
            save.mutate({ name: name.trim() });
          }}
        >
          {save.error ? (
            <div className="w-full">
              <ErrorNote message={message(save.error, 'Could not rename this period.')} />
            </div>
          ) : null}
          <label className="grid gap-1.5">
            <span className="eyebrow">Name</span>
            <input
              className="field w-full sm:w-64"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </label>
          <button className="btn btn-primary" type="submit" disabled={save.isPending || unchanged}>
            {save.isPending ? 'Saving…' : 'Save'}
          </button>
        </form>
      </section>
    </div>
  );
}
